import {
  IsString,
  IsNumber,
  IsIn,
  IsOptional,
  IsDefined,
  ValidateNested,
  Min,
  Max,
  IsBoolean,
} from 'class-validator';
import { Type } from 'class-transformer';
import { IsColorOrReference } from '@builder4/shared/dto/styles/validators/IsColorOrReference';
import { IsTRBLNumberCoords } from '@builder4/shared/dto/craft/validators/IsTRBLNumberCoords';
import { IsVideoUrl } from '@builder4/shared/dto/craft/validators/IsVideoUrl';
import { IsVideoEmbedCode } from '@builder4/shared/dto/craft/validators/IsVideoEmbedCode';
import { GradientDto, RgbaDto } from '@builder4/shared/dto/styles';
import { VIDEO_PROVIDER_VALUES } from '@builder4/shared/dto/craft/props/VideoPropsDto';
import { IMAGE_CONTINUOUS_TYPES } from '@builder4/shared/types/animation';

const BACKGROUND_IMAGE_MODES = ['fill', 'fit', 'stretch', 'tile'] as const;
const BACKGROUND_POSITIONS = [
  'left top',
  'center top',
  'right top',
  'left center',
  'center center',
  'right center',
  'left bottom',
  'center bottom',
  'right bottom',
] as const;
const VIDEO_SOURCE_TYPES = ['url', 'embed'] as const;
const VIDEO_FIT_TYPES = ['fill', 'fit'] as const;
const BREAKPOINT_VALUES = ['desktop', 'tablet', 'mobile'] as const;

export class PageBreakpointPropsDto {
  @IsOptional()
  @IsTRBLNumberCoords()
  padding?: [number, number, number, number];

  @IsOptional()
  @IsNumber()
  @Min(0)
  gap?: number;

  @IsOptional()
  @IsString()
  maxWidth?: string;
}

export class PagePropsDto {
  // Background color
  @IsOptional()
  @IsColorOrReference()
  backgroundColor!: RgbaDto | GradientDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => GradientDto)
  bgGradient?: GradientDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  bgSolid?: RgbaDto;

  // Background image
  @IsDefined()
  @IsNumber()
  @Min(1)
  @Max(500)
  bgImageScale!: number;

  @IsOptional()
  @IsString()
  bgImage?: string | null;

  @IsOptional()
  @IsString()
  bgImageId?: string | null;

  @IsDefined()
  @IsNumber()
  @Min(0)
  @Max(100)
  bgImageOpacity!: number;

  @IsDefined()
  @IsIn([...BACKGROUND_IMAGE_MODES])
  bgImageMode!: (typeof BACKGROUND_IMAGE_MODES)[number];

  @IsDefined()
  @IsIn([...BACKGROUND_POSITIONS])
  bgImagePosition!: (typeof BACKGROUND_POSITIONS)[number];

  // Background video
  @IsOptional()
  @IsIn([...VIDEO_SOURCE_TYPES])
  bgVideoSourceType?: (typeof VIDEO_SOURCE_TYPES)[number];

  @IsOptional()
  @IsVideoUrl()
  bgVideoUrl?: string | null;

  @IsOptional()
  @IsIn([...VIDEO_PROVIDER_VALUES])
  bgVideoProvider?: (typeof VIDEO_PROVIDER_VALUES)[number];

  @IsOptional()
  @IsString()
  bgVideoThumbnailUrl?: string | null;

  @IsOptional()
  @IsVideoEmbedCode()
  bgVideoEmbedCode?: string | null;

  @IsOptional()
  @IsIn([...VIDEO_FIT_TYPES])
  bgVideoMode?: (typeof VIDEO_FIT_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  bgVideoOpacity?: number;

  @IsOptional()
  @IsBoolean()
  bgVideoAutoPlay?: boolean;

  // Background image continuous animation
  @IsOptional()
  @IsIn([...IMAGE_CONTINUOUS_TYPES])
  bgContinuousAnimation?: (typeof IMAGE_CONTINUOUS_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(1)
  @Max(30)
  bgContinuousSpeed?: number;

  @IsOptional()
  @IsNumber()
  @Min(1)
  @Max(50)
  bgContinuousIntensity?: number;

  // Layout
  @IsDefined()
  @IsString()
  fillSpace!: string;

  @IsDefined()
  @IsString()
  maxWidth!: string;

  @IsDefined()
  @IsString()
  height!: string;

  @IsDefined()
  @IsTRBLNumberCoords()
  padding!: [number, number, number, number];

  @IsDefined()
  @IsNumber()
  @Min(0)
  radius!: number;

  @IsDefined()
  @IsNumber()
  @Min(0)
  gap!: number;

  // Breakpoint
  @IsOptional()
  @IsIn([...BREAKPOINT_VALUES])
  breakpoint?: (typeof BREAKPOINT_VALUES)[number];

  // Breakpoint overrides (optional, partial props)
  @IsOptional()
  @ValidateNested()
  @Type(() => PageBreakpointPropsDto)
  tablet?: PageBreakpointPropsDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => PageBreakpointPropsDto)
  mobile?: PageBreakpointPropsDto;
}
